import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { KidToggle } from '../components/KidToggle';
import { LoginForm } from '../components/LoginForm';
import { useNow } from '../hooks/useNow';
import { ApiError, api } from '../lib/api';
import type { Kid } from '../types';

type Auth = 'checking' | 'signed-out' | 'signed-in';

export function Admin() {
  const [auth, setAuth] = useState<Auth>('checking');
  const [kids, setKids] = useState<Kid[]>([]);
  const [notice, setNotice] = useState<{ text: string; error: boolean } | null>(null);
  const [expiredNotice, setExpiredNotice] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  useNow(); // keeps the "updated x ago" lines current

  /** A 401 mid-session means the cookie expired; return to the password form. */
  const handleError = useCallback((err: unknown) => {
    if (err instanceof ApiError && err.status === 401) {
      setAuth('signed-out');
      setExpiredNotice('Your session expired. Please sign in again.');
      return;
    }
    setNotice({ text: err instanceof Error ? err.message : 'Something went wrong.', error: true });
  }, []);

  const loadKids = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const { kids: fresh } = await api.getStatus(signal);
        if (!signal?.aborted) setKids(fresh);
      } catch (err) {
        if (!signal?.aborted) handleError(err);
      }
    },
    [handleError],
  );

  // Decide which panel to show before painting, so a signed-in user never sees
  // the password form flash past.
  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const { authenticated } = await api.getSession(controller.signal);
        if (controller.signal.aborted) return;
        setAuth(authenticated ? 'signed-in' : 'signed-out');
        if (authenticated) await loadKids(controller.signal);
      } catch {
        if (!controller.signal.aborted) setAuth('signed-out');
      }
    })();
    return () => controller.abort();
  }, [loadKids]);

  async function handleSignedIn() {
    setExpiredNotice(null);
    setAuth('signed-in');
    await loadKids();
  }

  async function handleSignOut() {
    try {
      await api.logout();
    } catch {
      // Signing out locally matters more than hearing back from the server.
    }
    setAuth('signed-out');
    setKids([]);
    setNotice(null);
  }

  async function handleToggle(slug: string, available: boolean) {
    setNotice(null);
    try {
      const { kid } = await api.setKid(slug, available);
      setKids((current) => current.map((entry) => (entry.slug === kid.slug ? kid : entry)));
    } catch (err) {
      handleError(err);
    }
  }

  async function handleSetAll(available: boolean) {
    setNotice(null);
    setBulkBusy(true);
    try {
      const { kids: updated } = await api.setAll(available);
      setKids(updated);
      setNotice({
        text: available ? 'Everyone is marked as free to play.' : 'Everyone is marked as busy.',
        error: false,
      });
    } catch (err) {
      handleError(err);
    } finally {
      setBulkBusy(false);
    }
  }

  if (auth === 'checking') {
    return (
      <main className="grid min-h-dvh place-items-center p-4">
        <p className="text-sm tracking-[0.08em] text-ink-muted uppercase">Loading…</p>
      </main>
    );
  }

  if (auth === 'signed-out') {
    return (
      <main className="grid min-h-dvh place-items-center p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]">
        <LoginForm onSignedIn={handleSignedIn} notice={expiredNotice} />
      </main>
    );
  }

  return (
    <main className="grid min-h-dvh place-items-center p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]">
      <section className="w-full max-w-lg rounded-2xl border border-white/10 bg-surface p-5 shadow-2xl sm:p-8">
        <header className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h1 className="m-0 text-2xl font-semibold tracking-tight">Who can play?</h1>
            <p className="mt-1.5 mb-0 text-sm text-ink-muted">Tap a name to flip that status.</p>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            className="shrink-0 cursor-pointer rounded-xl border border-white/10 px-3 py-2 text-sm text-ink-muted transition hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-300"
          >
            Sign out
          </button>
        </header>

        <ul className="m-0 mb-5 flex list-none flex-col gap-3 p-0">
          {kids.map((kid) => (
            <KidToggle key={kid.slug} kid={kid} onChange={handleToggle} />
          ))}
        </ul>

        <div className="grid grid-cols-1 gap-2.5 min-[430px]:grid-cols-2">
          <button
            type="button"
            disabled={bulkBusy}
            onClick={() => void handleSetAll(true)}
            className="min-h-11 cursor-pointer rounded-xl border border-play-edge bg-play px-4 py-3 font-semibold transition hover:brightness-110 disabled:cursor-progress disabled:opacity-55 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-300"
          >
            Everyone can play
          </button>
          <button
            type="button"
            disabled={bulkBusy}
            onClick={() => void handleSetAll(false)}
            className="min-h-11 cursor-pointer rounded-xl border border-busy-edge bg-busy px-4 py-3 font-semibold transition hover:brightness-110 disabled:cursor-progress disabled:opacity-55 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-300"
          >
            Nobody right now
          </button>
        </div>

        {notice && (
          <p
            role="status"
            className={[
              'mt-4 mb-0 rounded-lg px-3 py-2 text-center text-sm',
              notice.error ? 'bg-rose-950 text-rose-200' : 'bg-surface-raised text-ink-muted',
            ].join(' ')}
          >
            {notice.text}
          </p>
        )}

        <p className="mt-5 mb-0 text-center text-sm">
          <Link to="/" className="text-ink-muted no-underline hover:text-ink hover:underline">
            View the board →
          </Link>
        </p>
      </section>
    </main>
  );
}
